import React, { useState } from 'react';
import Icon from './ui-stubs';
import { Checkbox } from './ui-stubs';
import { Select } from './ui-stubs';
import { format } from 'date-fns';

const ListView = ({ tasks, selectedTasks, onTaskSelect, onSelectAll, onTaskClick, onTaskUpdate, density, getRiskLevel, isOverdue, calculateTaskAging }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [hoveredLiveTask, setHoveredLiveTask] = useState(null);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Done':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'In Review':
        return 'bg-purple-100 text-purple-800';
      case 'Blocked':
        return 'bg-red-100 text-red-800';
      case 'To Do':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-gray-400" />;
    }
    return sortConfig?.direction === 'asc'
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  const handleInlineStatusChange = (taskId, newStatus) => {
    const task = tasks?.find(t => t?.id === taskId);
    if (task) {
      onTaskUpdate({ ...task, status: newStatus });
    }
  };

  const statusOptions = [
    { value: 'To Do', label: 'To Do' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'In Review', label: 'In Review' },
    { value: 'Blocked', label: 'Blocked' },
    { value: 'Done', label: 'Done' },
  ];

  const getRiskBadge = (task) => {
    const risk = getRiskLevel(task);
    if (risk === 'low') return null;
    return {
      critical: { color: 'text-red-600', icon: 'AlertTriangle' },
      high: { color: 'text-orange-600', icon: 'AlertCircle' },
      medium: { color: 'text-yellow-600', icon: 'AlertCircle' },
    }?.[risk];
  };

  // Density-based row height
  const getRowHeight = () => {
    switch (density) {
      case 'ultra-compact':
        return 'py-1.5';
      case 'compact':
        return 'py-2';
      case 'comfortable':
      default:
        return 'py-3';
    }
  };

  const getTextSize = () => {
    switch (density) {
      case 'ultra-compact':
        return 'text-xs';
      case 'compact':
        return 'text-sm';
      case 'comfortable':
      default:
        return 'text-sm';
    }
  };

  const showDetails = density !== 'ultra-compact';

  if (tasks?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-12 text-center">
        <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks found</h3>
        <p className="text-gray-600">Try adjusting your filters or search terms</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-border sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={selectedTasks?.length === tasks?.length && tasks?.length > 0}
                  indeterminate={selectedTasks?.length > 0 && selectedTasks?.length < tasks?.length}
                  onChange={onSelectAll}
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center space-x-2">
                  <span>Task Title</span>
                  {getSortIcon('title')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-2">
                  <span>Status</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('priority')}
              >
                <div className="flex items-center space-x-2">
                  <span>Priority</span>
                  {getSortIcon('priority')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Task Owner
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('dueDate')}
              >
                <div className="flex items-center space-x-2">
                  <span>Due Date</span>
                  {getSortIcon('dueDate')}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Tags
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                Last Updated
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tasks?.map((task) => {
              const riskBadge = getRiskBadge(task);
              const aging = calculateTaskAging(task);

              return (
                <tr
                  key={task?.id}
                  className={`hover:bg-gray-50 transition-colors ${getRowHeight()}`}
                >
                  <td className="px-4">
                    <Checkbox
                      checked={selectedTasks?.includes(task?.id)}
                      onChange={() => onTaskSelect(task?.id)}
                    />
                  </td>
                  <td className="px-4">
                    <div className="flex items-start space-x-2">
                      {task?.dependencies?.length > 0 && (
                        <Icon name="Link" size={14} className="text-gray-400 mt-1" title="Has dependencies" />
                      )}
                      {riskBadge && (
                        <Icon name={riskBadge?.icon} size={14} className={`${riskBadge?.color} mt-1`} title="Risk indicator" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onTaskClick(task)}
                            className="text-sm font-semibold text-gray-900 hover:text-primary text-left"
                          >
                            {task?.title}
                          </button>
                          {task?.isLive && (
                            <div 
                              className="relative"
                              onMouseEnter={() => setHoveredLiveTask(task?.id)}
                              onMouseLeave={() => setHoveredLiveTask(null)}
                            >
                              <div className="relative w-2 h-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                              </div>
                              {hoveredLiveTask === task?.id && (
                                <div className="absolute left-0 top-6 z-50 w-64 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg">
                                  Users are actively working on this task right now.
                                  <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <span>{task?.id}</span>
                          {aging > 30 && (
                            <>
                              <span>•</span>
                              <span className="text-amber-600">{aging}d old</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4">
                    <div className="w-32">
                      <Select
                        value={task?.status}
                        onChange={(value) => handleInlineStatusChange(task?.id, value)}
                        options={statusOptions}
                      />
                    </div>
                  </td>
                  <td className="px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPriorityColor(task?.priority)}`}>
                      {task?.priority}
                    </span>
                  </td>
                  <td className="px-4">
                    <div className="flex items-center space-x-2">
                      {task?.owner ? (
                        <>
                          <div
                            className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold"
                            title={`${task?.owner?.name} - ${task?.owner?.role || ''}`}
                          >
                            {task?.owner?.avatar}
                          </div>
                          <span className="text-sm text-gray-700">{task?.owner?.name}</span>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400">Unassigned</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4">
                    <div className={`flex items-center space-x-1 text-xs ${
                      isOverdue(task) ? 'text-red-600 font-semibold' : 'text-gray-600'
                    }`}>
                      {isOverdue(task) && <Icon name="AlertCircle" size={12} />}
                      <span>{formatDate(task?.dueDate)}</span>
                    </div>
                  </td>
                  <td className="px-4">
                    <div className="flex flex-wrap gap-1">
                      {task?.tags?.slice(0, 2)?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                      {task?.tags?.length > 2 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          +{task?.tags?.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4">
                    <span className="text-xs text-gray-600">{formatDate(task?.lastUpdated)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListView;